import {
  createRouter,
  createWebHistory,
  type NavigationGuardNext,
  type RouteLocationNormalized,
  type RouteRecordRaw,
} from "vue-router";
import {
  addonNavigationGuard,
  authGuard,
  backOfficeGuard,
  cloudBackupWorkspaceGuard,
  deviceTablePageNavigationGuard,
  gatewaySettingsGuard,
  globalPermissionsPageTabGuard,
  globalWorkspacesGuard,
  moduleNavigationGuard,
  mspAccessControlPageTabGuard,
  mspPagePermissionGuard,
  mspPortalGuard,
  networkSettingsGuard,
  networkAnalyticsGuard,
  permissionsGuard,
  permissionsPageTabGuard,
  socGuard,
  socTicketsLogGuard,
  usersTablePageNavigationGuard,
  workspaceFrozenGuard,
  workspaceInitialGuard,
} from "@/_helpers/navigation-guards";
import { PsaProviders } from "@/constants/connectors";
import {
  SubscriptionAddon,
  SubscriptionModule,
  WorkspaceLocale,
  WorkspaceType,
} from "@/constants/workplaces";
import {
  GlobalRoleScopes,
  GlobalRoleScopeSection,
  MspPortalScopeSection,
  MspRolePermissionScopes,
  ProtectionScopeModules,
  RolePermissionsScope,
  RoleType,
  WorkspaceManagementScopeSections,
} from "@/constants/roles";
import { RouteName, RouteLayout } from "@/constants/routes";
import { useAccountStore } from "@/_store/account.module.ts";
import { getWorkspaceFromRedirect } from "@/_helpers/navigation";
import { setWorkspace } from "@/_helpers/workspace";
import { getBrowserLocale } from "@/_helpers/dom";
import type { Service } from "@/constants/cloud-apps";
import { useRedirectsStore } from "@/_store/redirects.module";
import type { WorkspaceDetails } from "@/_store/workspaces-list.module.ts";
import { useMspStore } from "@/_store/msp/msp.module.ts";
import { useTutorialStore } from "@/_store/tutorial.module.ts";
import { useGtm } from "@gtm-support/vue-gtm";
import { i18n } from "@/plugins/i18n.ts";
import { GtmAction } from "@/constants/gtm.ts";
import { useAppsManagementModule } from "@/_store/apps-mgmt.module.ts";
import { useWorkspaceStore } from "@/_store/workspace.module.ts";
import { isStaleChunkError } from "@/_helpers/stale-chunk.ts";
import { loadRecaptcha } from "@/_helpers/recaptcha";

const browserLocale: WorkspaceLocale = (() => {
  try {
    return getBrowserLocale();
  } catch {
    return WorkspaceLocale.EN_US;
  }
})();

const routes: RouteRecordRaw[] = [
  {
    name: RouteName.PORTAL,
    path: "/portal",
    redirect: { name: RouteName.DASHBOARD_PAGE },
    beforeEnter: async (_, __, next) => {
      const appsMgmtStore = useAppsManagementModule();
      const workspaceStore = useWorkspaceStore();
      const accountStore = useAccountStore();
      const workspaceId = workspaceStore.workspace.workspaceId;
      if (appsMgmtStore.singleWorkspaceId && workspaceId !== appsMgmtStore.singleWorkspaceId) {
        // not using updateWorkspaceAndPermissions not to trigger additional requests
        // which will come as a result of timestamp change
        workspaceStore.$patch({
          workspace: {
            workspaceId: appsMgmtStore.singleWorkspaceId,
          },
        });
        await workspaceStore.getCurrentWorkspace();
      }
      if (accountStore.logged && workspaceId) {
        await accountStore.checkMspAccess();
        accountStore.checkWorkspaceQuantityForUser();
      }
      return next();
    },
    children: [
      {
        path: "soc-tickets",
        name: RouteName.SOC_TICKETS,
        component: () => import("@/views/tickets/SocTicketsPage.vue"),
        beforeEnter: [socTicketsLogGuard],
      },
      {
        path: "global-workspace-management",
        name: RouteName.GLOBAL_WORKSPACE_MANAGEMENT_PAGE,
        component: () => import("@/views/GlobalWorkspaceManagementPage.vue"),
        beforeEnter: [globalWorkspacesGuard],
        props: (route) => ({ search: route.query.search }),
      },
      {
        path: "back-office",
        name: RouteName.BACK_OFFICE_PAGE,
        component: () => import("@/views/back-office/BackOfficePage.vue"),
        beforeEnter: [backOfficeGuard()],
        redirect: { name: RouteName.BACKOFFICE_DASHBOARD },
        children: [
          {
            path: "dashboard",
            name: RouteName.BACKOFFICE_DASHBOARD,
            component: () => import("@/views/back-office/BackOfficeDashboardPage.vue"),
            beforeEnter: [backOfficeGuard()],
          },
          {
            path: "permissions",
            name: RouteName.BACKOFFICE_PERMISSIONS_PAGE,
            component: () => import("@/views/back-office/BackOfficePermissionsPage.vue"),
            redirect: { name: RouteName.GLOBAL_ADMIN_USERS },
            beforeEnter: [backOfficeGuard()],
            children: [
              {
                path: "admin-users",
                name: RouteName.GLOBAL_ADMIN_USERS,
                component: () => import("@/views/back-office/GlobalAdminUsersTab.vue"),
                beforeEnter: [
                  backOfficeGuard(
                    GlobalRoleScopes.GLOBAL_SCOPE,
                    GlobalRoleScopeSection.GLOBAL_ADMIN_USERS
                  ),
                  globalPermissionsPageTabGuard(GlobalRoleScopeSection.GLOBAL_ADMIN_USERS),
                ],
              },
              {
                path: "roles",
                name: RouteName.GLOBAL_ROLES,
                component: () => import("@/views/back-office/GlobalRolesTab.vue"),
                beforeEnter: [
                  backOfficeGuard(
                    GlobalRoleScopes.GLOBAL_SCOPE,
                    GlobalRoleScopeSection.GLOBAL_ROLES
                  ),
                  globalPermissionsPageTabGuard(GlobalRoleScopeSection.GLOBAL_ROLES),
                ],
                props: {
                  roleType: RoleType.GLOBAL,
                },
              },
            ],
          },
          {
            path: "agent-gradual-update",
            name: RouteName.BACK_OFFICE_AGENT_GRADUAL_UPDATE,
            component: () => import("@/views/back-office/AgentsGradualUpdatePage.vue"),
            beforeEnter: [
              backOfficeGuard(
                GlobalRoleScopes.GLOBAL_SCOPE,
                GlobalRoleScopeSection.SPECIAL_PERMISSIONS,
                "manageEndpointAutoUpdateGroups"
              ),
            ],
          },
          {
            path: "discovery-stats",
            name: RouteName.BACK_OFFICE_DISCOVERY_STATS,
            component: () => import("@/views/back-office/DiscoveryStatsPage.vue"),
            beforeEnter: [backOfficeGuard()],
          },
          {
            path: "global-allow-block-list",
            name: RouteName.BACK_OFFICE_GLOBAL_ALLOW_BLOCK_LIST,
            component: () => import("@/views/back-office/GlobalAllowBlockListPage.vue"),
            beforeEnter: [backOfficeGuard()],
          },
        ],
      },
      {
        path: "settings",
        name: RouteName.SETTINGS,
        component: () => import("@/views/ControlPanelPage.vue"),
      },
      {
        path: "settings/cloud-backup",
        name: RouteName.CLOUD_BACKUP_PAGE,
        component: () => import("@/views/settings/cloud-backup/BackupAndRestorePage.vue"),
        redirect: { name: RouteName.CLOUD_BACKUP_OVERVIEW_TAB },
        beforeEnter: [
          moduleNavigationGuard(SubscriptionModule.BACKUP_AND_RESTORE),
          permissionsGuard(RolePermissionsScope.PROTECTION, SubscriptionModule.BACKUP_AND_RESTORE),
          cloudBackupWorkspaceGuard(),
        ],
        children: [
          {
            path: "overview",
            name: RouteName.CLOUD_BACKUP_OVERVIEW_TAB,
            component: () => import("@/views/settings/cloud-backup/OverviewTab.vue"),
            beforeEnter: [
              moduleNavigationGuard(SubscriptionModule.BACKUP_AND_RESTORE),
              permissionsGuard(
                RolePermissionsScope.PROTECTION,
                SubscriptionModule.BACKUP_AND_RESTORE
              ),
              cloudBackupWorkspaceGuard(),
            ],
          },
          {
            path: "backup",
            name: RouteName.CLOUD_BACKUP_BACKUPS_TAB,
            component: () => import("@/views/settings/cloud-backup/BackupJobsTab.vue"),
            beforeEnter: [
              moduleNavigationGuard(SubscriptionModule.BACKUP_AND_RESTORE),
              permissionsGuard(
                RolePermissionsScope.PROTECTION,
                SubscriptionModule.BACKUP_AND_RESTORE
              ),
              cloudBackupWorkspaceGuard(),
            ],
          },
          {
            path: "restore",
            name: RouteName.CLOUD_BACKUP_RESTORES_TAB,
            component: () => import("@/views/settings/cloud-backup/RestoreJobsTab.vue"),
            beforeEnter: [
              moduleNavigationGuard(SubscriptionModule.BACKUP_AND_RESTORE),
              permissionsGuard(
                RolePermissionsScope.PROTECTION,
                SubscriptionModule.BACKUP_AND_RESTORE
              ),
              cloudBackupWorkspaceGuard(),
            ],
          },
          {
            path: "connected-apps",
            name: RouteName.CLOUD_BACKUP_CONNECTED_APPS_TAB,
            component: () => import("@/views/settings/cloud-backup/ConnectedAppsTab.vue"),
            beforeEnter: [
              moduleNavigationGuard(SubscriptionModule.BACKUP_AND_RESTORE),
              permissionsGuard(
                RolePermissionsScope.PROTECTION,
                SubscriptionModule.BACKUP_AND_RESTORE
              ),
              cloudBackupWorkspaceGuard(),
            ],
          },
        ],
      },
      {
        path: "settings/endpoint-security",
        name: RouteName.ENDPOINT_SECURITY_SETTINGS_PAGE,
        component: () => import("@/views/settings/endpoint-security/EndpointSecurityPage.vue"),
        redirect: { name: RouteName.ENDPOINT_SECURITY_DEVICE_POSTURE_TAB },
        beforeEnter: [
          moduleNavigationGuard(SubscriptionModule.ENDPOINT_SECURITY),
          permissionsGuard(RolePermissionsScope.PROTECTION, SubscriptionModule.ENDPOINT_SECURITY),
        ],
        children: [
          {
            path: "device-posture",
            name: RouteName.ENDPOINT_SECURITY_DEVICE_POSTURE_TAB,
            component: () =>
              import("@/views/settings/endpoint-security/EndpointSecurityDevicePostureTab.vue"),
            beforeEnter: [
              moduleNavigationGuard(SubscriptionModule.ENDPOINT_SECURITY),
              permissionsGuard(
                RolePermissionsScope.PROTECTION,
                SubscriptionModule.ENDPOINT_SECURITY
              ),
            ],
          },
          {
            path: "device-policy",
            name: RouteName.ENDPOINT_SECURITY_DEVICE_POLICY_TAB,
            component: () =>
              import("@/views/settings/endpoint-security/EndpointSecurityDevicePolicyTab.vue"),
            beforeEnter: [
              moduleNavigationGuard(SubscriptionModule.ENDPOINT_SECURITY),
              permissionsGuard(
                RolePermissionsScope.PROTECTION,
                SubscriptionModule.ENDPOINT_SECURITY
              ),
            ],
          },
          {
            path: "ngav",
            name: RouteName.ENDPOINT_SECURITY_NGAV_TAB,
            component: () =>
              import("@/views/settings/endpoint-security/EndpointSecurityNgavTab.vue"),
            beforeEnter: [
              moduleNavigationGuard(SubscriptionModule.ENDPOINT_SECURITY),
              permissionsGuard(
                RolePermissionsScope.PROTECTION,
                SubscriptionModule.ENDPOINT_SECURITY
              ),
            ],
          },
          {
            path: "allow-block-list",
            name: RouteName.ENDPOINT_SECURITY_ALLOW_BLOCK_LIST_TAB,
            component: () =>
              import("@/views/settings/endpoint-security/EndpointSecurityAllowBlockListTab.vue"),
            beforeEnter: [
              moduleNavigationGuard(SubscriptionModule.ENDPOINT_SECURITY),
              permissionsGuard(
                RolePermissionsScope.PROTECTION,
                SubscriptionModule.ENDPOINT_SECURITY
              ),
            ],
          },
          {
            path: "malware-scan",
            name: RouteName.ENDPOINT_SECURITY_MALWARE_SCAN_TAB,
            component: () =>
              import("@/views/settings/endpoint-security/EndpointSecurityMalwareScanTab.vue"),
            beforeEnter: [
              moduleNavigationGuard(SubscriptionModule.ENDPOINT_SECURITY),
              permissionsGuard(
                RolePermissionsScope.PROTECTION,
                SubscriptionModule.ENDPOINT_SECURITY
              ),
            ],
          },
          {
            path: "add-ons",
            name: RouteName.ENDPOINT_SECURITY_DEVICE_ADDONS_TAB,
            component: () =>
              import("@/views/settings/endpoint-security/EndpointSecurityAddonsTab.vue"),
            beforeEnter: [
              addonNavigationGuard(SubscriptionAddon.WIFI_PHISHING),
              permissionsGuard(
                RolePermissionsScope.PROTECTION,
                SubscriptionModule.ENDPOINT_SECURITY
              ),
            ],
          },
        ],
      },
      {
        path: "settings/endpoint-data-governance-settings",
        name: RouteName.ENDPOINT_DATA_GOVERNANCE_PAGE,
        component: () => import("@/views/settings/endpoint-data-governance/SettingsPage.vue"),
        redirect: {
          name: RouteName.ENDPOINT_DATA_GOVERNANCE_PRIVACY_SENSITIVE_DATA_TAB,
        },
        beforeEnter: [
          moduleNavigationGuard(SubscriptionModule.ENDPOINT_DATA_GOVERNANCE),
          permissionsGuard(
            RolePermissionsScope.PROTECTION,
            SubscriptionModule.ENDPOINT_DATA_GOVERNANCE
          ),
        ],
        children: [
          {
            path: "privacy-sensitive-data",
            name: RouteName.ENDPOINT_DATA_GOVERNANCE_PRIVACY_SENSITIVE_DATA_TAB,
            component: () =>
              import("@/views/settings/endpoint-data-governance/PrivacySensitiveDataTab.vue"),
            beforeEnter: [
              moduleNavigationGuard(SubscriptionModule.ENDPOINT_DATA_GOVERNANCE),
              permissionsGuard(
                RolePermissionsScope.PROTECTION,
                SubscriptionModule.ENDPOINT_DATA_GOVERNANCE
              ),
            ],
          },
          {
            path: "sensitive-data-scans",
            name: RouteName.ENDPOINT_DATA_GOVERNANCE_SENSITIVE_DATA_SCANS_TAB,
            component: () =>
              import("@/views/settings/endpoint-data-governance/SensitiveDataScansTab.vue"),
            beforeEnter: [
              moduleNavigationGuard(SubscriptionModule.ENDPOINT_DATA_GOVERNANCE),
              permissionsGuard(
                RolePermissionsScope.PROTECTION,
                SubscriptionModule.ENDPOINT_DATA_GOVERNANCE
              ),
            ],
          },
          {
            path: "exclusions",
            name: RouteName.ENDPOINT_DATA_GOVERNANCE_EXCLUSIONS_TAB,
            component: () => import("@/views/settings/endpoint-data-governance/ExclusionsTab.vue"),
            beforeEnter: [
              moduleNavigationGuard(SubscriptionModule.ENDPOINT_DATA_GOVERNANCE),
              permissionsGuard(
                RolePermissionsScope.PROTECTION,
                SubscriptionModule.ENDPOINT_DATA_GOVERNANCE
              ),
            ],
          },
        ],
      },
      {
        path: "settings/devices",
        name: RouteName.DEVICES_SETTINGS_PAGE,
        meta: {
          titleKey: "appBar.title.devices",
        },
        component: () => import("@/views/settings/devices/DevicesSettingsPage.vue"),
        redirect: { name: RouteName.DEVICES_SETTINGS_AGENT_DEPLOYMENT_TAB }, // default child path
        children: [
          {
            path: "agent-deployment",
            name: RouteName.DEVICES_SETTINGS_AGENT_DEPLOYMENT_TAB,
            component: () => import("@/views/settings/devices/AgentDeploymentTab.vue"),
            beforeEnter: [
              permissionsGuard(
                RolePermissionsScope.WORKSPACE_MANAGEMENT,
                WorkspaceManagementScopeSections.DEVICES
              ),
            ],
          },
          {
            path: "device-settings",
            name: RouteName.DEVICES_SETTINGS_DEVICE_SETTINGS_TAB,
            component: () => import("@/views/settings/devices/DevicesSettingsTab.vue"),
            beforeEnter: [
              permissionsGuard(
                RolePermissionsScope.WORKSPACE_MANAGEMENT,
                WorkspaceManagementScopeSections.DEVICES
              ),
            ],
          },
          {
            path: "labels",
            name: RouteName.DEVICES_SETTINGS_LABELS_TAB,
            component: () => import("@/views/settings/devices/DevicesLabelsTab.vue"),
            beforeEnter: [
              permissionsGuard(
                RolePermissionsScope.WORKSPACE_MANAGEMENT,
                WorkspaceManagementScopeSections.DEVICES
              ),
            ],
          },
        ],
      },
      {
        path: "settings/network-settings",
        name: RouteName.NETWORK_SETTINGS_GENERAL,
        component: () => import("@/views/settings/network/NetworkSettingsPage.vue"),
        beforeEnter: [networkSettingsGuard],
      },
      {
        path: "settings/gateway",
        name: RouteName.GATEWAY_SETTINGS,
        component: () => import("@/views/settings/gateway-settings/GatewaySettingsPage.vue"),
        beforeEnter: [gatewaySettingsGuard],
        redirect: { name: RouteName.GATEWAY_SETTINGS_INBOUND },
        children: [
          {
            path: "inbound",
            name: RouteName.GATEWAY_SETTINGS_INBOUND,
            component: () => import("@/views/settings/gateway-settings/InboundGatewayTab.vue"),
          },
          {
            path: "outbound",
            name: RouteName.GATEWAY_SETTINGS_OUTBOUND,
            component: () => import("@/views/settings/gateway-settings/OutboundGatewayTab.vue"),
            beforeEnter: [
              permissionsGuard(
                RolePermissionsScope.PROTECTION,
                SubscriptionModule.USER_DATA_GOVERNANCE
              ),
              moduleNavigationGuard(SubscriptionModule.USER_DATA_GOVERNANCE),
            ],
          },
        ],
      },
      {
        path: "settings/access-control/admin-user/:id",
        name: RouteName.ACCESS_CONTROL_ADMIN_USER_DETAILS, // Update to appropriate RouteName value if available
        component: () => import("@/views/settings/access-control/AdminUserDetails.vue"),
        props: (route) => ({ adminUserId: route.params.id }),
        beforeEnter: permissionsGuard(
          RolePermissionsScope.WORKSPACE_MANAGEMENT,
          WorkspaceManagementScopeSections.ADMIN_USERS
        ),
      },
      {
        path: "settings/connectors",
        name: RouteName.CONNECTORS_PAGE,
        component: () => import("@/views/settings/connectors/ConnectorsPage.vue"),
        redirect: { name: RouteName.CONNECTORS_API_KEYS_TAB },
        beforeEnter: [
          permissionsGuard(
            RolePermissionsScope.WORKSPACE_MANAGEMENT,
            WorkspaceManagementScopeSections.CONNECTORS
          ),
        ],
        children: [
          {
            path: "siem",
            name: RouteName.CONNECTORS_SIEM_TAB,
            component: () => import("@/views/settings/connectors/SiemTab.vue"),
            beforeEnter: [
              permissionsGuard(
                RolePermissionsScope.WORKSPACE_MANAGEMENT,
                WorkspaceManagementScopeSections.CONNECTORS
              ),
            ],
          },
          {
            path: "api-keys",
            name: RouteName.CONNECTORS_API_KEYS_TAB,
            component: () => import("@/views/settings/connectors/ApiCredentialsTab.vue"),
            beforeEnter: [
              permissionsGuard(
                RolePermissionsScope.WORKSPACE_MANAGEMENT,
                WorkspaceManagementScopeSections.CONNECTORS
              ),
            ],
          },
          {
            path: "webhooks",
            name: RouteName.CONNECTORS_WEBHOOKS_TAB,
            component: () => import("@/views/settings/connectors/WebhooksTab.vue"),
            beforeEnter: [
              permissionsGuard(
                RolePermissionsScope.WORKSPACE_MANAGEMENT,
                WorkspaceManagementScopeSections.CONNECTORS
              ),
            ],
          },
          {
            path: "psa",
            name: RouteName.CONNECTORS_PSA_TAB,
            component: () => import("@/views/settings/connectors/PsaTab.vue"),
            beforeEnter: [
              permissionsGuard(
                RolePermissionsScope.WORKSPACE_MANAGEMENT,
                WorkspaceManagementScopeSections.CONNECTORS
              ),
            ],
          },
          {
            path: "syslog",
            name: RouteName.CONNECTORS_SYSLOG,
            component: () => import("@/views/settings/connectors/SyslogTab.vue"),
            beforeEnter: [
              permissionsGuard(
                RolePermissionsScope.WORKSPACE_MANAGEMENT,
                WorkspaceManagementScopeSections.CONNECTORS
              ),
            ],
          },
        ],
      },
      {
        path: "settings/connectors/connectwise",
        name: RouteName.CONNECTORS_CONNECTWISE,
        component: () => import("@/views/settings/connectors/PsaConnectorConfigurationPage.vue"),
        props: {
          provider: PsaProviders.CONNECTWISE,
        },
        redirect: {
          name: RouteName.CONNECTORS_CONNECTWISE_WORKSPACE_MAPPING,
        },
        beforeEnter: [
          permissionsGuard(
            RolePermissionsScope.WORKSPACE_MANAGEMENT,
            WorkspaceManagementScopeSections.CONNECTORS
          ),
        ],
        children: [
          {
            path: "workspace-mapping",
            name: RouteName.CONNECTORS_CONNECTWISE_WORKSPACE_MAPPING,
            component: () => import("@/views/settings/connectors/WorkspaceMappingPage.vue"),
            beforeEnter: [
              permissionsGuard(
                RolePermissionsScope.WORKSPACE_MANAGEMENT,
                WorkspaceManagementScopeSections.CONNECTORS
              ),
            ],
          },
          {
            path: "service-mapping",
            name: RouteName.CONNECTORS_CONNECTWISE_SERVICE_MAPPING,
            component: () => import("@/views/settings/connectors/ServiceMappingPage.vue"),
            beforeEnter: [
              permissionsGuard(
                RolePermissionsScope.WORKSPACE_MANAGEMENT,
                WorkspaceManagementScopeSections.CONNECTORS
              ),
            ],
          },
          {
            path: "ticket-mapping",
            name: RouteName.CONNECTORS_CONNECTWISE_TICKETING,
            component: () => import("@/views/settings/connectors/PsaTicketsMappingPage.vue"),
            beforeEnter: [
              permissionsGuard(
                RolePermissionsScope.WORKSPACE_MANAGEMENT,
                WorkspaceManagementScopeSections.CONNECTORS
              ),
            ],
          },
        ],
      },
      {
        path: "settings/connectors/autotask",
        name: RouteName.CONNECTORS_AUTOTASK,
        component: () => import("@/views/settings/connectors/PsaConnectorConfigurationPage.vue"),
        props: {
          provider: PsaProviders.AUTOTASK,
        },
        redirect: { name: RouteName.CONNECTORS_AUTOTASK_WORKSPACE_MAPPING },

        beforeEnter: [
          permissionsGuard(
            RolePermissionsScope.WORKSPACE_MANAGEMENT,
            WorkspaceManagementScopeSections.CONNECTORS
          ),
        ],
        children: [
          {
            path: "workspace-mapping",
            name: RouteName.CONNECTORS_AUTOTASK_WORKSPACE_MAPPING,
            component: () => import("@/views/settings/connectors/WorkspaceMappingPage.vue"),
            beforeEnter: [
              permissionsGuard(
                RolePermissionsScope.WORKSPACE_MANAGEMENT,
                WorkspaceManagementScopeSections.CONNECTORS
              ),
            ],
          },
          {
            path: "service-mapping",
            name: RouteName.CONNECTORS_AUTOTASK_SERVICE_MAPPING,
            component: () => import("@/views/settings/connectors/ServiceMappingPage.vue"),
            beforeEnter: [
              permissionsGuard(
                RolePermissionsScope.WORKSPACE_MANAGEMENT,
                WorkspaceManagementScopeSections.CONNECTORS
              ),
            ],
          },
          {
            path: "ticketing",
            name: RouteName.CONNECTORS_AUTOTASK_TICKETING,
            component: () => import("@/views/Placeholder.vue"),
            beforeEnter: [
              permissionsGuard(
                RolePermissionsScope.WORKSPACE_MANAGEMENT,
                WorkspaceManagementScopeSections.CONNECTORS
              ),
            ],
          },
        ],
      },
      {
        path: "settings/user-data-governance-settings",
        name: RouteName.USER_DATA_GOVERNANCE_PAGE,
        component: () => import("@/views/settings/user-data-governance/UserDataGovernancePage.vue"),
        redirect: { name: RouteName.USER_DATA_GOVERNANCE_MONITORING_TAB },
        children: [
          {
            path: "monitoring",
            name: RouteName.USER_DATA_GOVERNANCE_MONITORING_TAB,
            component: () => import("@/views/settings/user-data-governance/UserDataMonitorTab.vue"),
            beforeEnter: [
              permissionsGuard(
                RolePermissionsScope.PROTECTION,
                SubscriptionModule.USER_DATA_GOVERNANCE
              ),
              moduleNavigationGuard(SubscriptionModule.USER_DATA_GOVERNANCE),
            ],
          },
          {
            path: "permissions",
            name: RouteName.USER_DATA_GOVERNANCE_PERMISSIONS_TAB,
            component: () =>
              import("@/views/settings/user-data-governance/UserDataPermissionsTab.vue"),
            beforeEnter: [
              permissionsGuard(
                RolePermissionsScope.PROTECTION,
                SubscriptionModule.USER_DATA_GOVERNANCE
              ),
              moduleNavigationGuard(SubscriptionModule.USER_DATA_GOVERNANCE),
            ],
          },
          {
            path: "exclusions",
            name: RouteName.USER_DATA_GOVERNANCE_EXCLUSIONS_TAB,
            component: () =>
              import("@/views/settings/user-data-governance/UserDataExclusionsTab.vue"),
            beforeEnter: [
              permissionsGuard(
                RolePermissionsScope.PROTECTION,
                SubscriptionModule.USER_DATA_GOVERNANCE
              ),
              moduleNavigationGuard(SubscriptionModule.USER_DATA_GOVERNANCE),
            ],
          },
        ],
      },
      {
        path: "settings/email",
        name: RouteName.EMAIL_SECURITY_PAGE,
        component: () => import("@/views/settings/email-security/EmailSecurityPage.vue"),
        redirect: { name: RouteName.EMAIL_SECURITY_SETTINGS_TAB },
        children: [
          {
            path: "email-settings",
            name: RouteName.EMAIL_SECURITY_SETTINGS_TAB,
            component: () => import("@/views/settings/email-security/EmailSettingsTab.vue"),
            beforeEnter: [moduleNavigationGuard(SubscriptionModule.EMAIL_SECURITY)],
          },
          {
            path: "allow-block-list",
            name: RouteName.EMAIL_SECURITY_ALLOW_BLOCK_LIST_TAB,
            component: () =>
              import("@/views/settings/email-security/EmailSecurityAllowBlockTab.vue"),
            beforeEnter: [moduleNavigationGuard(SubscriptionModule.EMAIL_SECURITY)],
          },
          {
            path: "add-ins",
            name: RouteName.EMAIL_SECURITY_ADD_INS_TAB,
            component: () => import("@/views/settings/email-security/EmailSecurityAddIns.vue"),
            beforeEnter: [moduleNavigationGuard(SubscriptionModule.EMAIL_SECURITY)],
          },
          {
            path: "coro-mail",
            name: RouteName.EMAIL_SECURITY_CORO_MAIL_TAB,
            component: () =>
              import("@/views/settings/email-security/EmailSecuritySecureMessagesTab.vue"),
            beforeEnter: [addonNavigationGuard(SubscriptionAddon.SECURED_MESSAGES)],
          },
        ],
      },
      {
        path: "settings/cloud-security",
        name: RouteName.CLOUD_SECURITY_PAGE,
        component: () => import("@/views/settings/cloud-security/CloudSecurityPage.vue"),
        redirect: { name: RouteName.CLOUD_SECURITY_PERMISSIONS_LIST_TAB },
        beforeEnter: [
          moduleNavigationGuard(SubscriptionModule.CLOUD_SECURITY),
          permissionsGuard(RolePermissionsScope.PROTECTION, SubscriptionModule.CLOUD_SECURITY),
        ],
        children: [
          {
            path: "permissions-list",
            name: RouteName.CLOUD_SECURITY_PERMISSIONS_LIST_TAB,
            component: () =>
              import("@/views/settings/cloud-security/cloud-security-tabs/AccessPermissionsTab.vue"),
            beforeEnter: [
              moduleNavigationGuard(SubscriptionModule.CLOUD_SECURITY),
              permissionsGuard(RolePermissionsScope.PROTECTION, SubscriptionModule.CLOUD_SECURITY),
            ],
          },
          {
            path: "third-party-apps",
            name: RouteName.CLOUD_SECURITY_THIRD_PARTY_APPS_TAB,
            component: () =>
              import("@/views/settings/cloud-security/cloud-security-tabs/ThirdPartyAppsTab.vue"),
            beforeEnter: [
              moduleNavigationGuard(SubscriptionModule.CLOUD_SECURITY),
              permissionsGuard(RolePermissionsScope.PROTECTION, SubscriptionModule.CLOUD_SECURITY),
            ],
          },
          {
            path: "settings",
            name: RouteName.CLOUD_SECURITY_SETTINGS,
            component: () =>
              import("@/views/settings/cloud-security/cloud-security-tabs/CloudSecuritySettingsTab.vue"),
            beforeEnter: [
              moduleNavigationGuard(SubscriptionModule.CLOUD_SECURITY),
              permissionsGuard(RolePermissionsScope.PROTECTION, SubscriptionModule.CLOUD_SECURITY),
            ],
          },
        ],
      },
      {
        path: "settings/activity-logs",
        name: RouteName.ACTIVITY_LOGS,
        component: () => import("@/views/settings/activity-logs/ActivityLogsPage.vue"),
        beforeEnter: [
          permissionsGuard(
            RolePermissionsScope.WORKSPACE_MANAGEMENT,
            WorkspaceManagementScopeSections.ACTIVITY_LOGS
          ),
        ],
      },

      {
        path: "settings/soc",
        name: RouteName.SOC_PAGE,
        component: () => import("@/views/settings/soc/SocPage.vue"),
        redirect: { name: RouteName.SOC_CONTACTS_TAB },
        beforeEnter: [
          socGuard,
          permissionsGuard(RolePermissionsScope.PROTECTION, ProtectionScopeModules.MANAGED_SOC),
        ],
        children: [
          {
            path: "soc-contacts",
            name: RouteName.SOC_CONTACTS_TAB,
            component: () => import("@/views/settings/soc/SocContactsPage.vue"),
            beforeEnter: [
              socGuard,
              permissionsGuard(RolePermissionsScope.PROTECTION, ProtectionScopeModules.MANAGED_SOC),
            ],
          },
          {
            path: "soc-remediation",
            name: RouteName.SOC_REMEDIATION_TAB,
            component: () => import("@/views/settings/soc/SocRemediationPage.vue"),
            beforeEnter: [
              socGuard,
              permissionsGuard(RolePermissionsScope.PROTECTION, ProtectionScopeModules.MANAGED_SOC),
            ],
          },
        ],
      },
      {
        path: "settings/security-awareness",
        name: RouteName.SECURITY_AWARENESS,
        component: () => import("@/views/settings/security-awareness/SecurityAwarenessPage.vue"),
        redirect: { name: RouteName.PHISHING_SIMULATION },
        beforeEnter: [
          permissionsGuard(
            RolePermissionsScope.PROTECTION,
            SubscriptionModule.SECURITY_AWARENESS_TRAINING
          ),
        ],
        children: [
          {
            path: "phishing-simulation",
            name: RouteName.PHISHING_SIMULATION,
            component: () => import("@/views/settings/security-awareness/PhishingSettingsTab.vue"),
            beforeEnter: [
              permissionsGuard(
                RolePermissionsScope.PROTECTION,
                SubscriptionModule.SECURITY_AWARENESS_TRAINING
              ),
            ],
          },
          {
            path: "security-training",
            name: RouteName.SECURITY_TRAINING,
            component: () =>
              import("@/views/settings/security-awareness/SecurityTrainingSettingsTab.vue"),
            beforeEnter: [
              permissionsGuard(
                RolePermissionsScope.PROTECTION,
                SubscriptionModule.SECURITY_AWARENESS_TRAINING
              ),
            ],
          },
          {
            path: "settings",
            name: RouteName.SECURITY_AWARENESS_SETTINGS,
            component: () =>
              import("@/views/settings/security-awareness/SecurityAwarenessSettingsTab.vue"),
            beforeEnter: [
              permissionsGuard(
                RolePermissionsScope.PROTECTION,
                SubscriptionModule.SECURITY_AWARENESS_TRAINING
              ),
            ],
          },
        ],
      },
      {
        path: "settings/users",
        name: RouteName.USERS_SETTINGS_PAGE,
        component: () => import("@/views/settings/users/UsersSettingsPage.vue"),
        redirect: { name: RouteName.USERS_SETTINGS_USERS_TAB },
        beforeEnter: [
          permissionsGuard(
            RolePermissionsScope.WORKSPACE_MANAGEMENT,
            WorkspaceManagementScopeSections.USERS
          ),
        ],
        children: [
          {
            path: "protected-users",
            name: RouteName.USERS_SETTINGS_USERS_TAB,
            component: () => import("@/views/settings/users/UsersTab.vue"),
            beforeEnter: [
              permissionsGuard(
                RolePermissionsScope.WORKSPACE_MANAGEMENT,
                WorkspaceManagementScopeSections.USERS
              ),
            ],
          },
          {
            path: "protected-groups",
            name: RouteName.USERS_SETTINGS_GROUPS_TAB,
            component: () => import("@/views/settings/users/UserGroupsTab.vue"),
            beforeEnter: [
              permissionsGuard(
                RolePermissionsScope.WORKSPACE_MANAGEMENT,
                WorkspaceManagementScopeSections.USERS
              ),
            ],
          },
          {
            path: "labels",
            name: RouteName.USERS_SETTINGS_LABELS_TAB,
            component: () => import("@/views/settings/users/UsersLabelsTab.vue"),
            beforeEnter: [
              permissionsGuard(
                RolePermissionsScope.WORKSPACE_MANAGEMENT,
                WorkspaceManagementScopeSections.USERS
              ),
            ],
          },
        ],
      },
      {
        path: "settings/swg",
        name: RouteName.SECURE_WEB_GATEWAY_SETTINGS,
        component: () => import("@/views/settings/swg/SecureWebGatewaySettings.vue"),
      },
      {
        path: "settings/network",
        name: RouteName.NETWORK_PAGE,
        component: () => import("@/views/settings/network/NetworkPage.vue"),
        // Overview is the landing tab. When the environment does not serve it, its own guard bounces to
        // Virtual Office — a `redirect` cannot make that call, because it is resolved before guards run
        // and so cannot await the feature flags.
        redirect: { name: RouteName.NETWORK_OVERVIEW_TAB },
        children: [
          {
            path: "overview",
            name: RouteName.NETWORK_OVERVIEW_TAB,
            component: () => import("@/views/settings/network/NetworkOverviewTab.vue"),
            beforeEnter: networkAnalyticsGuard,
          },
          {
            path: "office",
            name: RouteName.NETWORK_VIRTUAL_OFFICE,
            component: () => import("@/views/settings/network/VirtualOfficeTab.vue"),
            children: [
              {
                path: "vpn",
                name: RouteName.NETWORK_VPN_TAB,
                component: () =>
                  import("@/views/settings/network/VirtualOfficeVpnConfigurationTab.vue"),
              },
              {
                path: "ztna",
                name: RouteName.ZTNA_TAB,
                component: () =>
                  import("@/views/settings/network/VirtualOfficeZtnaConfigurationTab.vue"),
              },
            ],
          },
          {
            path: "settings",
            name: RouteName.NETWORK_SETTINGS_TAB,
            component: () => import("@/views/settings/network/NetworkSettingsTab.vue"),
          },
          {
            path: "site-to-site",
            name: RouteName.NETWORK_SITE_TO_SITE_TAB,
            component: () => import("@/views/settings/network/SiteToSiteTab.vue"),
          },
        ],
      },
      {
        path: "settings/reports",
        name: RouteName.REPORTS_PAGE,
        component: () => import("@/views/settings/reports/ReportsPage.vue"),
        redirect: { name: RouteName.REPORTS_SELECT_REPORTS },
        children: [
          {
            path: "select",
            name: RouteName.REPORTS_SELECT_REPORTS,
            component: () => import("@/views/settings/reports/ReportsTab.vue"),
            beforeEnter: permissionsGuard(
              RolePermissionsScope.WORKSPACE_MANAGEMENT,
              WorkspaceManagementScopeSections.REPORTS
            ),
          },
          {
            path: "exports-tab",
            name: RouteName.REPORTS_EXPORTS_TAB,
            component: () => import("@/views/settings/reports/ExportsTab.vue"),
            beforeEnter: permissionsGuard(
              RolePermissionsScope.WORKSPACE_MANAGEMENT,
              WorkspaceManagementScopeSections.REPORTS
            ),
          },
          {
            path: "scheduled-tab",
            name: RouteName.REPORTS_SCHEDULED_TAB,
            component: () => import("@/views/settings/reports/ScheduledReportsTab.vue"),
            beforeEnter: permissionsGuard(
              RolePermissionsScope.WORKSPACE_MANAGEMENT,
              WorkspaceManagementScopeSections.REPORTS
            ),
          },
          {
            path: "executive-summary",
            name: RouteName.REPORTS_EXECUTIVE_SUMMARY_TAB,
            component: () => import("@/views/settings/reports/ExecutiveSummaryReportsTab.vue"),
            beforeEnter: permissionsGuard(
              RolePermissionsScope.WORKSPACE_MANAGEMENT,
              WorkspaceManagementScopeSections.REPORTS
            ),
          },
          {
            path: "security-awareness-training",
            name: RouteName.REPORTS_SECURITY_AWARENESS_TAB,
            component: () => import("@/views/settings/reports/SecurityTrainingSummaryTab.vue"),
            beforeEnter: permissionsGuard(
              RolePermissionsScope.PROTECTION,
              SubscriptionModule.SECURITY_AWARENESS_TRAINING
            ),
          },
          {
            path: "dns-summary",
            name: RouteName.REPORTS_DNS_SUMMARY_TAB,
            component: () => import("@/views/settings/reports/DnsSummaryTab.vue"),
            beforeEnter: [
              moduleNavigationGuard(SubscriptionModule.SWG),
              permissionsGuard(
                RolePermissionsScope.WORKSPACE_MANAGEMENT,
                WorkspaceManagementScopeSections.REPORTS
              ),
              permissionsGuard(RolePermissionsScope.PROTECTION, SubscriptionModule.NETWORK),
              permissionsGuard(RolePermissionsScope.PROTECTION, SubscriptionModule.SWG),
            ],
          },
          {
            path: "managed-services-summary",
            name: RouteName.REPORTS_MANAGED_SERVICES_SUMMARY_TAB,
            component: () => import("@/views/settings/reports/ManagedServicesSummaryTab.vue"),
            beforeEnter: socGuard,
          },
          {
            path: "secured-messages-summary",
            name: RouteName.REPORTS_SECURED_MESSAGES_SUMMARY_TAB,
            component: () => import("@/views/settings/reports/SecuredMessagesSummaryTab.vue"),
            beforeEnter: [
              addonNavigationGuard(SubscriptionAddon.SECURED_MESSAGES),
              permissionsGuard(
                RolePermissionsScope.WORKSPACE_MANAGEMENT,
                WorkspaceManagementScopeSections.REPORTS
              ),
            ],
          },
          {
            path: "phishing-simulation-summary",
            name: RouteName.REPORTS_PHISHING_SIMULATION_SUMMARY_TAB,
            component: () => import("@/views/settings/reports/PhishingSimulationSummaryTab.vue"),
            beforeEnter: permissionsGuard(
              RolePermissionsScope.PROTECTION,
              SubscriptionModule.SECURITY_AWARENESS_TRAINING
            ),
          },
          {
            path: "warning-only-email-events",
            name: RouteName.REPORTS_WARNING_ONLY_EMAIL_EVENTS_TAB,
            component: () => import("@/views/settings/reports/WarningOnlyEmailEventsTab.vue"),
            beforeEnter: permissionsGuard(
              RolePermissionsScope.WORKSPACE_MANAGEMENT,
              WorkspaceManagementScopeSections.REPORTS
            ),
          },
        ],
        beforeEnter: permissionsGuard(
          RolePermissionsScope.WORKSPACE_MANAGEMENT,
          WorkspaceManagementScopeSections.REPORTS
        ),
      },
      {
        path: "settings/cloud-apps",
        name: RouteName.CLOUD_APPS_PAGE,
        component: () => import("@/views/settings/cloud-apps/CloudAppsPage.vue"),
      },
      {
        path: "settings/access-control",
        name: RouteName.ACCESS_CONTROL_PAGE,
        component: () => import("@/views/settings/access-control/AccessControlPage.vue"),
        redirect: { name: RouteName.ACCESS_CONTROL_ADMIN_USERS_TAB },
        children: [
          {
            path: "admin-users",
            name: RouteName.ACCESS_CONTROL_ADMIN_USERS_TAB,
            component: () => import("@/views/settings/access-control/AdminUsersTab.vue"),
            beforeEnter: [permissionsPageTabGuard(WorkspaceManagementScopeSections.ADMIN_USERS)],
          },
          {
            path: "roles",
            name: RouteName.ACCESS_CONTROL_ROLES_TAB,
            component: () => import("@/views/settings/access-control/RolesTab.vue"),
            props: {
              roleType: RoleType.WORKSPACE,
            },
            beforeEnter: [permissionsPageTabGuard(WorkspaceManagementScopeSections.ROLES)],
          },
          {
            path: "active-sessions",
            name: RouteName.ACCESS_CONTROL_ACTIVE_SESSIONS_TAB,
            component: () => import("@/views/settings/access-control/ActiveSessionsTab.vue"),
            beforeEnter: [
              permissionsPageTabGuard(WorkspaceManagementScopeSections.ACTIVE_SESSIONS),
            ],
          },
        ],
      },
      {
        path: "edr",
        name: RouteName.EDR_PAGE,
        component: () => import("@/views/settings/edr/EdrPage.vue"),
        redirect: { name: RouteName.EDR_SETTINGS_TAB },
        beforeEnter: [
          moduleNavigationGuard(SubscriptionModule.EDR),
          permissionsGuard(RolePermissionsScope.PROTECTION, SubscriptionModule.EDR),
        ],
        children: [
          {
            path: "processes",
            name: RouteName.EDR_PROCESSES_TAB,
            component: () => import("@/views/settings/edr/ProcessesTab.vue"),
            beforeEnter: [
              moduleNavigationGuard(SubscriptionModule.EDR),
              permissionsGuard(RolePermissionsScope.PROTECTION, SubscriptionModule.EDR),
            ],
          },
          {
            path: "telemetry",
            name: RouteName.EDR_TELEMETRY_TAB,
            component: () => import("@/views/settings/edr/TelemetryTab.vue"),
            beforeEnter: [
              moduleNavigationGuard(SubscriptionModule.EDR),
              permissionsGuard(RolePermissionsScope.PROTECTION, SubscriptionModule.EDR),
            ],
          },
          {
            path: "allow-block-list",
            name: RouteName.EDR_ALLOW_BLOCK_LIST_TAB,
            component: () => import("@/views/settings/edr/EdrAllowBlockListTab.vue"),
            beforeEnter: [
              moduleNavigationGuard(SubscriptionModule.EDR),
              permissionsGuard(RolePermissionsScope.PROTECTION, SubscriptionModule.EDR),
            ],
          },
          {
            path: "settings",
            name: RouteName.EDR_SETTINGS_TAB,
            component: () => import("@/views/settings/edr/EdrSettingsTab.vue"),
            beforeEnter: [
              moduleNavigationGuard(SubscriptionModule.EDR),
              permissionsGuard(RolePermissionsScope.PROTECTION, SubscriptionModule.EDR),
            ],
          },
        ],
      },
      {
        path: "dashboard",
        name: RouteName.DASHBOARD_PAGE,
        component: () => import("@/views/dashboard/DashboardPage.vue"),
      },
      {
        path: "security-gaps",
        name: RouteName.SECURITY_GAPS_PAGE,
        component: () => import("@/views/SecurityGapsPage.vue"),
        beforeEnter: [
          workspaceFrozenGuard(RouteName.DASHBOARD_PAGE),
          workspaceInitialGuard(RouteName.DASHBOARD_PAGE),
        ],
      },
      {
        path: "coro-impact",
        name: RouteName.CORO_IMPACT_PAGE,
        component: () => import("@/views/CoroImpactTab.vue"),
      },
      {
        path: "devices",
        name: RouteName.DEVICES,
        component: () => import("@/views/DevicesPage.vue"),
        beforeEnter: [
          deviceTablePageNavigationGuard,
          permissionsGuard(RolePermissionsScope.VIEWS, "devicesView"),
        ],
        props: (route) => ({
          hasVulnerabilities: route.query.hasVulnerabilities,
          search: route.query.search,
          widget: route.query.widget,
          agentHealth: route.query.agentHealth,
        }),
      },
      {
        path: "protected-users",
        name: RouteName.PROTECTED_USERS,
        beforeEnter: [
          usersTablePageNavigationGuard,
          permissionsGuard(RolePermissionsScope.VIEWS, "usersView"),
        ],
        component: () => import("@/views/UsersPage.vue"),
        props: (route) => ({
          hasVulnerabilities: route.query.hasVulnerabilities,
        }),
      },
      {
        path: "alerts",
        name: RouteName.ALERTS,
        component: () => import("@/views/MessagesPage.vue"),
      },
      {
        path: "process-graph",
        name: RouteName.PROCESS_GRAPH,
        component: () => import("@/views/ProcessGraph.vue"),
        props: (route) => ({
          enrollmentCode: route.query.enrollmentCode,
          processHash: route.query.processHash,
          processCreationTime: route.query.processCreationTime,
          pid: route.query.pid,
          eventId: route.query.eventId,
        }),
        beforeEnter: [
          moduleNavigationGuard(SubscriptionModule.EDR),
          permissionsGuard(RolePermissionsScope.PROTECTION, SubscriptionModule.EDR),
        ],
      },
      {
        path: "tickets",
        name: RouteName.TICKETS,
        component: () => import("@/views/tickets/TicketsPage.vue"),
      },
      {
        // when /portal/event/id is matched, redirect to tickets with search by id
        path: "event/:id",
        redirect: (to) => ({
          name: RouteName.TICKETS,
          query: { search: to.params.id },
        }),
      },
      {
        path: "tickets/details/abnormal-data-manipulation",
        name: RouteName.ABNORMAL_DATA_MANIPULATION_DETAILS_PAGE,
        component: () => import("@/views/tickets/AbnormalDataManipulationDetailsPage.vue"),
        props: (route) => ({
          ticketId: route.query.ticketId,
          path: route.query.path,
          pathId: route.query.pathId,
          eventTrigger: route.query.eventTrigger,
        }),
      },
      {
        path: "my-account",
        name: RouteName.MY_ACCOUNT_PAGE,
        component: () => import("@/views/my-account/MyAccountPage.vue"),
        redirect: { name: RouteName.MY_ACCOUNT_PROFILE_TAB },
        children: [
          {
            path: "profile",
            name: RouteName.MY_ACCOUNT_PROFILE_TAB,
            component: () => import("@/views/my-account/ProfileTab.vue"),
          },
          {
            path: "change-password",
            name: RouteName.MY_ACCOUNT_CHANGE_PASSWORD_TAB,
            component: () => import("@/views/my-account/ChangePasswordTab.vue"),
          },
          {
            path: "mfa",
            name: RouteName.MY_ACCOUNT_MFA_TAB,
            component: () => import("@/views/my-account/MfaTab.vue"),
          },
          {
            path: "notifications",
            name: RouteName.MY_ACCOUNT_NOTIFICATIONS_TAB,
            component: () => import("@/views/my-account/NotificationsTab.vue"),
          },
        ],
      },
      {
        path: "workplaces",
        redirect: { name: RouteName.WORKSPACES },
      },
      {
        path: "workspaces",
        name: RouteName.WORKSPACES,
        component: () => import("@/views/WorkspacesPage.vue"),
      },
    ],
  },
  {
    name: RouteName.MSP,
    path: "/msp",
    redirect: { name: RouteName.MSP_DASHBOARD_PAGE },
    beforeEnter: [
      mspPortalGuard,
      async (_, __, next) => {
        const mspStore = useMspStore();
        const appsMgmtStore = useAppsManagementModule();
        const workspaceStore = useWorkspaceStore();
        const workspaceId = workspaceStore.workspace.workspaceId;
        if (appsMgmtStore.mspWorkspaceId && workspaceId !== appsMgmtStore.mspWorkspaceId) {
          // not using updateWorkspaceAndPermissions not to trigger additional requests
          // which will come as a result of timestamp change
          workspaceStore.$patch({ workspace: { workspaceId: appsMgmtStore.mspWorkspaceId } });
          await workspaceStore.getCurrentWorkspace();
        } else if (!appsMgmtStore.mspWorkspaceId) {
          await mspStore.getRelevantHierarchy();
        }
        return next();
      },
    ],
    children: [
      {
        path: "dashboard",
        name: RouteName.MSP_DASHBOARD_PAGE,
        component: () => import("@/views/msp/MspDashboardPage.vue"),
      },
      {
        path: "security-gaps",
        name: RouteName.MSP_SECURITY_GAPS_PAGE,
        component: () => import("@/views/SecurityGapsPage.vue"),
        beforeEnter: [
          workspaceFrozenGuard(RouteName.MSP_DASHBOARD_PAGE),
          workspaceInitialGuard(RouteName.MSP_DASHBOARD_PAGE),
        ],
      },
      {
        path: "coro-impact",
        name: RouteName.MSP_CORO_IMPACT_PAGE,
        component: () => import("@/views/MspCoroImpactTab.vue"),
      },
      {
        path: RouteName.MSP_SOC_TICKETS,
        name: RouteName.MSP_SOC_TICKETS,
        component: () => import("@/views/tickets/SocTicketsPage.vue"),
        beforeEnter: [socTicketsLogGuard],
      },
      {
        path: "global-workspace-management",
        name: RouteName.MSP_GLOBAL_WORKSPACE_MANAGEMENT_PAGE,
        component: () => import("@/views/GlobalWorkspaceManagementPage.vue"),
        beforeEnter: [globalWorkspacesGuard],
        props: (route) => ({ search: route.query.search }),
      },
      {
        path: "back-office",
        name: RouteName.MSP_BACK_OFFICE_PAGE,
        component: () => import("@/views/back-office/BackOfficePage.vue"),
        beforeEnter: [backOfficeGuard()],
        redirect: { name: RouteName.MSP_BACKOFFICE_DASHBOARD },
        children: [
          {
            path: "dashboard",
            name: RouteName.MSP_BACKOFFICE_DASHBOARD,
            component: () => import("@/views/back-office/BackOfficeDashboardPage.vue"),
            beforeEnter: [backOfficeGuard()],
          },
          {
            path: "permissions",
            name: RouteName.MSP_BACKOFFICE_PERMISSIONS_PAGE,
            component: () => import("@/views/back-office/BackOfficePermissionsPage.vue"),
            redirect: { name: RouteName.MSP_GLOBAL_ADMIN_USERS },
            beforeEnter: [backOfficeGuard()],
            children: [
              {
                path: "admin-users",
                name: RouteName.MSP_GLOBAL_ADMIN_USERS,
                component: () => import("@/views/back-office/GlobalAdminUsersTab.vue"),
                beforeEnter: [
                  backOfficeGuard(
                    GlobalRoleScopes.GLOBAL_SCOPE,
                    GlobalRoleScopeSection.GLOBAL_ADMIN_USERS
                  ),
                  globalPermissionsPageTabGuard(GlobalRoleScopeSection.GLOBAL_ADMIN_USERS),
                ],
              },
              {
                path: "global-roles",
                name: RouteName.MSP_GLOBAL_ROLES,
                component: () => import("@/views/back-office/GlobalRolesTab.vue"),
                beforeEnter: [
                  backOfficeGuard(
                    GlobalRoleScopes.GLOBAL_SCOPE,
                    GlobalRoleScopeSection.GLOBAL_ROLES
                  ),
                  globalPermissionsPageTabGuard(GlobalRoleScopeSection.GLOBAL_ROLES),
                ],
                props: {
                  roleType: RoleType.GLOBAL,
                },
              },
            ],
          },
          {
            path: "agent-gradual-update",
            name: RouteName.MSP_BACK_OFFICE_AGENT_GRADUAL_UPDATE,
            component: () => import("@/views/back-office/AgentsGradualUpdatePage.vue"),
            beforeEnter: [
              backOfficeGuard(
                GlobalRoleScopes.GLOBAL_SCOPE,
                GlobalRoleScopeSection.SPECIAL_PERMISSIONS,
                "manageEndpointAutoUpdateGroups"
              ),
            ],
          },
          {
            path: "discovery-stats",
            name: RouteName.MSP_BACK_OFFICE_DISCOVERY_STATS,
            component: () => import("@/views/back-office/DiscoveryStatsPage.vue"),
            beforeEnter: [backOfficeGuard()],
          },
          {
            path: "global-allow-block-list",
            name: RouteName.MSP_BACK_OFFICE_GLOBAL_ALLOW_BLOCK_LIST,
            component: () => import("@/views/back-office/GlobalAllowBlockListPage.vue"),
            beforeEnter: [backOfficeGuard()],
          },
        ],
      },
      {
        path: "workspaces",
        name: RouteName.MSP_WORKSPACES_PAGE,
        component: () => import("@/views/msp/MspWorkspacesPage.vue"),
        redirect: { name: RouteName.MSP_WORKSPACES_TREEVIEW_TAB },
        children: [
          {
            path: "list",
            name: RouteName.MSP_WORKSPACES_LIST_TAB,
            component: () => import("@/views/msp/MspWorkspacesListTab.vue"),
            props: (route) => ({
              search: route.query.search,
            }),
            beforeEnter: [
              mspPagePermissionGuard(
                MspRolePermissionScopes.MSP_PORTAL_SCOPE,
                MspPortalScopeSection.MSP_WORKSPACES
              ),
            ],
          },
          {
            path: "treeview",
            name: RouteName.MSP_WORKSPACES_TREEVIEW_TAB,
            component: () => import("@/views/msp/MspWorkspacesTreeviewTab.vue"),
            beforeEnter: [
              mspPagePermissionGuard(
                MspRolePermissionScopes.MSP_PORTAL_SCOPE,
                MspPortalScopeSection.MSP_WORKSPACES
              ),
            ],
          },
        ],
      },

      {
        name: RouteName.MSP_TICKETS_PAGE,
        path: "tickets",
        component: () => import("@/views/msp/MspTicketsPage.vue"),
      },
      {
        name: RouteName.MSP_PROTECTED_USERS_PAGE,
        path: "protected-users",
        component: () => import("@/views/msp/MspUsersPage.vue"),
        beforeEnter: [mspPagePermissionGuard(RolePermissionsScope.VIEWS, "usersView")],
      },
      {
        path: "global-devices",
        name: RouteName.MSP_DEVICES_PAGE,
        component: () => import("@/views/msp/MspDevicesPage.vue"),
        redirect: { name: RouteName.MSP_DEVICES_TAB },
        beforeEnter: [mspPagePermissionGuard(RolePermissionsScope.VIEWS, "devicesView")],
        children: [
          {
            name: RouteName.MSP_DEVICES_TAB,
            path: "devices",
            component: () => import("@/views/msp/MspDevicesTab.vue"),
            props: (route) => ({
              search: route.query.search,
            }),
          },
          {
            name: RouteName.MSP_DRIVES_TAB,
            path: "drives",
            component: () => import("@/views/msp/MspDrivesTab.vue"),
          },
        ],
      },
      {
        name: RouteName.MSP_CONTROL_PANEL_PAGE,
        path: "settings",
        component: () => import("@/views/msp/control-panel/MspGlobalControlPanelPage.vue"),
      },
      {
        name: RouteName.MSP_LABELS,
        path: "settings/labels",
        component: () => import("@/views/msp/control-panel/MspLabelsPage.vue"),
        beforeEnter: [
          mspPagePermissionGuard(
            MspRolePermissionScopes.MSP_PORTAL_SCOPE,
            MspPortalScopeSection.MSP_WORKSPACE_LABELS
          ),
        ],
        props: (route) => ({
          search: route.query.search,
        }),
      },
      {
        name: RouteName.MSP_CLOUD_BACKUP,
        path: "settings/cloud-backup",
        component: () =>
          import("@/views/msp/control-panel/cloud-backup/MspCloudBackupAndRestorePage.vue"),
        beforeEnter: [
          mspPagePermissionGuard(
            MspRolePermissionScopes.MSP_PORTAL_SCOPE,
            MspPortalScopeSection.MSP_BACKUP_AND_RESTORE
          ),
          moduleNavigationGuard(SubscriptionModule.BACKUP_AND_RESTORE),
        ],
      },
      {
        path: "settings/global-policies",
        name: RouteName.MSP_GLOBAL_POLICIES,
        component: () =>
          import("@/views/msp/control-panel/global-policies/MspGlobalPoliciesPage.vue"),
        beforeEnter: [
          mspPagePermissionGuard(
            MspRolePermissionScopes.MSP_PORTAL_SCOPE,
            MspPortalScopeSection.MSP_GLOBAL_POLICIES
          ),
        ],
      },
      {
        path: "settings/reports",
        name: RouteName.MSP_REPORTS,
        component: () => import("@/views/msp/control-panel/reports/MspReportsPage.vue"),
        redirect: { name: RouteName.MSP_REPORTS_LIST_TAB },
        beforeEnter: [
          mspPagePermissionGuard(
            MspRolePermissionScopes.MSP_PORTAL_SCOPE,
            MspPortalScopeSection.MSP_REPORTS
          ),
        ],
        children: [
          {
            path: "select",
            name: RouteName.MSP_REPORTS_LIST_TAB,
            component: () => import("@/views/msp/control-panel/reports/MspReportsTab.vue"),
          },
          {
            path: "exports",
            name: RouteName.MSP_REPORTS_EXPORTS_TAB,
            component: () => import("@/views/msp/control-panel/reports/MspExportsTab.vue"),
          },
          {
            path: "warning-only-email-events",
            name: RouteName.MSP_REPORTS_WARNING_ONLY_EMAIL_EVENTS_TAB,
            component: () =>
              import("@/views/msp/control-panel/reports/MspWarningOnlyEmailEventsTab.vue"),
          },
        ],
      },
      {
        path: "settings/allow-block-lists",
        name: RouteName.MSP_ALLOW_BLOCK_LISTS,
        component: () =>
          import("@/views/msp/control-panel/allow-block-lists/MspAllowBlockListsPage.vue"),
        redirect: { name: RouteName.MSP_ALLOW_BLOCK_LISTS_EMAIL_SECURITY_TAB },
        beforeEnter: [
          mspPagePermissionGuard(
            MspRolePermissionScopes.MSP_PORTAL_SCOPE,
            MspPortalScopeSection.MSP_ALLOW_BLOCK_LISTS
          ),
        ],
        children: [
          {
            path: "email-security",
            name: RouteName.MSP_ALLOW_BLOCK_LISTS_EMAIL_SECURITY_TAB,
            component: () =>
              import("@/views/msp/control-panel/allow-block-lists/MspEmailSecurityAllowBlockListTab.vue"),
          },
          {
            path: "endpoint-security",
            name: RouteName.MSP_ALLOW_BLOCK_LISTS_ENDPOINT_SECURITY_TAB,
            component: () =>
              import("@/views/msp/control-panel/allow-block-lists/MspEndpointSecurityAllowBlockListTab.vue"),
          },
          {
            path: "edr",
            name: RouteName.MSP_ALLOW_BLOCK_LISTS_EDR_TAB,
            component: () =>
              import("@/views/msp/control-panel/allow-block-lists/MspEdrAllowBlockListTab.vue"),
          },
        ],
      },
      {
        path: "settings/access-control",
        name: RouteName.MSP_ACCESS_CONTROL_PAGE,
        component: () =>
          import("@/views/msp/control-panel/access-control/MspAccessControlPage.vue"),
        redirect: { name: RouteName.MSP_ADMIN_USERS_TAB },
        children: [
          {
            path: "admin-users",
            name: RouteName.MSP_ADMIN_USERS_TAB,
            component: () =>
              import("@/views/msp/control-panel/access-control/MspAdminUsersTab.vue"),
            beforeEnter: [mspAccessControlPageTabGuard(MspPortalScopeSection.MSP_ADMIN_USERS)],
          },
          {
            path: "roles",
            name: RouteName.MSP_ROLES_TAB,
            component: () => import("@/views/msp/control-panel/access-control/MspRolesTab.vue"),
            beforeEnter: [mspAccessControlPageTabGuard(MspPortalScopeSection.MSP_ROLES)],
            props: {
              roleType: RoleType.MSP,
            },
          },
        ],
      },
    ],
  },
  {
    path: "/login",
    name: RouteName.LOGIN,
    component: () => import("@/views/LoginPage.vue"),
    meta: { publicPage: true, preferredLocale: browserLocale, preloadRecaptcha: true },
  },
  {
    path: "/invited",
    name: RouteName.INVITED_PAGE,
    component: () => import("@/views/InvitedPage.vue"),
    meta: { publicPage: true, preferredLocale: browserLocale },
  },
  {
    path: "/sign-up",
    name: RouteName.SIGN_UP,
    component: () => import("@/views/SignUpPage.vue"),
    meta: { publicPage: true, preferredLocale: browserLocale, preloadRecaptcha: true },
  },
  {
    path: "/forgot-password",
    name: RouteName.FORGOT_PASSWORD,
    component: () => import("@/views/ForgotPasswordPage.vue"),
    meta: { publicPage: true, preferredLocale: browserLocale },
  },
  {
    path: "/reset-password/:id",
    name: RouteName.RESET_PASSWORD,
    props: (route) => ({ token: window.atob(route.params.id as string) }),
    component: () => import("@/views/ResetPasswordPage.vue"),
    meta: { publicPage: true, alwaysPublicPage: true, preferredLocale: browserLocale },
  },
  {
    path: "/mfa",
    name: RouteName.MFA_PAGE,
    component: () => import("@/views/MfaPage.vue"),
    meta: { publicPage: true, preferredLocale: browserLocale },
  },
  {
    path: "/verification",
    name: RouteName.MFA_VERIFICATION,
    component: () => import("@/views/MfaVerificationPage.vue"),
    meta: { publicPage: true, preferredLocale: browserLocale },
  },
  {
    path: "/activate-mobile",
    name: RouteName.ACTIVATE_MOBILE_PAGE,
    component: () => import("@/views/ActivateMobilePage.vue"),
    props: (route) => ({
      mobileToken: route.query.mobileToken,
    }),
    meta: { publicPage: true, preferredLocale: browserLocale },
  },
  {
    path: "/mdm/callback",
    name: RouteName.MDM_CALLBACK_PAGE,
    component: () => import("@/views/MdmCallbackPage.vue"),
    beforeEnter: moduleNavigationGuard(SubscriptionModule.MDM),
    props: (route) => ({
      error: route.query.error,
    }),
    meta: { publicPage: true },
  },
  {
    path: "/proxy-verification",
    name: RouteName.PROXY_VERIFICATION_PAGE,
    component: () => import("@/views/ProxyVerificationPage.vue"),
    meta: { publicPage: true, alwaysPublicPage: true, preferredLocale: browserLocale },
  },
  {
    path: "/email-verification",
    name: RouteName.EMAIL_VERIFICATION_PAGE,
    component: () => import("@/views/EmailVerificationPage.vue"),
    meta: { publicPage: true, alwaysPublicPage: true, preferredLocale: browserLocale },
  },
  {
    path: "/pni-verification",
    name: RouteName.PNI_VERIFICATION_PAGE,
    component: () => import("@/views/PniVerificationPage.vue"),
    meta: { publicPage: true, alwaysPublicPage: true, preferredLocale: browserLocale },
    props: (route) => {
      const { allowList, token, syncToken, emailAddress } = route.query;
      return { allowListString: allowList, token, syncToken, emailAddress };
    },
  },
  {
    path: "/downloads/:id",
    name: RouteName.DOWNLOADS_PAGE,
    component: () => import("@/views/DownloadsPage.vue"),
    meta: { publicPage: true, alwaysPublicPage: true },
    props: (route) => ({ code: route.params.id }),
  },
  {
    path: "/auth/transfer",
    name: RouteName.SSO_PAGE,
    meta: { publicPage: true },
    component: () => import("@/views/SsoPage.vue"),
  },
  {
    path: "/mcp/oauth/consent",
    name: RouteName.OAUTH_CONSENT,
    component: () => import("@/views/OAuthConsentPage.vue"),
    meta: { preferredLocale: browserLocale, layout: RouteLayout.BLANK },
  },
  {
    path: "/:catchAll(.*)",
    redirect: { name: RouteName.DASHBOARD_PAGE },
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to, from, next) => {
  if (to.query.workspaceId) {
    await handleWorkspaceChangeViaQuery(to, from, next);
  }

  await saveDataFromReport(to);
  await saveDataFromSocAutomation(to);
  await saveDataToDashboard(to);
  await saveDataToControlPanel(to);

  loginWithUrlParams(to, next);
  authGuard(to, from, next);
  next();
});

// Not awaited: the page must not wait for a third-party script. A failure is surfaced on submit,
// where `getRecaptchaToken` loads the script again.
router.beforeEach((to) => {
  if (to.meta.preloadRecaptcha) loadRecaptcha().catch(() => {});
});

// After a new deployment, Vite generates new content-hashed chunk filenames.
// Users who had the app open before the deploy will have the old index.html referencing
// old chunk hashes that no longer exist on the server, causing dynamic imports to fail.
// Forcing a hard navigation reloads index.html and resolves the correct chunk filenames.
router.onError((error, to) => {
  // Firefox and Safari word this differently from Chromium, so matching the
  // Chromium string alone left those users on a silently dead route. See PLAT-4152.
  if (isStaleChunkError(error.message)) {
    window.location.assign(to.fullPath);
  }
});

export default router;

const loginWithUrlParams = async (to: RouteLocationNormalized, next: NavigationGuardNext) => {
  const accountStore = useAccountStore();
  const workspaceStore = useWorkspaceStore();
  const tutorialStore = useTutorialStore();
  const { token } = accountStore;
  const storeToken = token;
  const {
    accessToken,
    refreshToken,
    email,
    customerId,
    customerName,
    isNextStep,
    step,
    service,
    languageCode,
    isNewUser,
  } = to.query as Record<string, string>;
  const workplace = to.name === RouteName.WORKSPACES ? "" : customerId;

  if (accessToken && storeToken !== accessToken) {
    accountStore.$patch({
      socialLogin: true,
      token: accessToken,
      refreshToken,
      email,
      service: service as Service,
      hasMultipleWorkspaces: !workplace,
    });

    workspaceStore.$patch({
      workspace: {
        workspaceId: workplace,
        name: customerName,
      },
    });

    tutorialStore.$patch({
      isNewUser: isNewUser === "true",
    });

    // sending gtm event on sign up complete from cloud service
    if (isNewUser === "true") {
      const gtm = useGtm();
      if (gtm?.enabled()) {
        window.dataLayer?.push({
          event: `${GtmAction.SIGNUP_CLICKED} - ${i18n.global.t(service)}`,
        });
      }
    }

    if (workplace) {
      await workspaceStore.updateWorkspaceAndPermissions(workplace, false);
    }

    if (isNextStep) {
      return next({
        ...to,
        query: { isNextStep, service, step, languageCode },
        replace: true,
      });
    }

    return next({
      path: to.path,
    });
  }
};

const handleWorkspaceChangeViaQuery = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
): Promise<void> => {
  const workspaceStore = useWorkspaceStore();
  const currentWorkspaceId = workspaceStore.workspace.workspaceId;
  const { workspaceId } = to.query;
  // If workspaceId is present and different from the current workspace
  if (workspaceId && currentWorkspaceId !== workspaceId) {
    const workspace = await getWorkspaceFromRedirect(workspaceId as string);

    // If workspace is found, set it and call next
    if (workspace) {
      await setWorkspace(workspace);
      const { workspaceId, ...restQuery } = to.query; // Destructure to remove `workspaceId`
      return next({
        ...to,
        query: restQuery, // Use the updated query without `workspaceId`
        replace: true, // Replace the current history entry to avoid duplication in the history
      });
    }
  }

  // Call next() only once after all checks
  return next();
};

async function saveDataFromReport(to: RouteLocationNormalized) {
  const {
    navigationFromReport: isNavigatedFromReport,
    workspace,
    workspaceName,
    workspaceDomain,
    headerIconUrl,
    workspaceType,
  } = to.query as Record<string, string>;
  const navigationFromReport = isNavigatedFromReport === "true";
  if (navigationFromReport) {
    const redirectsStore = useRedirectsStore();
    const newWorkspace: Partial<WorkspaceDetails> = {
      workspaceId: workspace,
      name: workspaceName,
      type: workspaceType as WorkspaceType,
      domain: workspaceDomain,
      headerIconUrl: headerIconUrl,
    };
    redirectsStore.$patch({
      redirectUrl: to.fullPath,
      redirectPath: to.path,
      workspace: newWorkspace,
      navigationFromReport,
      navigationFromSocAutomation: false,
    });
    await redirectsStore.handleRedirectFromReport();
  }
}

async function saveDataFromSocAutomation(to: RouteLocationNormalized) {
  const {
    navigationFromSocAutomation: isNavigatedFromSocAutomation,
    workspaceId,
    action,
    policyType,
    enrollmentCode,
    osType,
  } = to.query as Record<string, string>;
  const navigationFromSocAutomation = isNavigatedFromSocAutomation === "true";
  if (workspaceId && navigationFromSocAutomation) {
    const redirectsStore = useRedirectsStore();
    redirectsStore.$patch({
      redirectUrl: to.fullPath,
      workspaceId: workspaceId,
      navigationFromReport: false,
      navigationFromSocAutomation,
    });

    await redirectsStore.handleRedirectFromSocAutomation({
      workspaceId,
      action,
      policyType,
      enrollmentCode,
      osType,
    });
  }
}

async function saveDataToDashboard(to: RouteLocationNormalized) {
  const { forceReload, navigationFromMdm, workspaceId } = to.query as Record<string, string>;
  const isNavigationToDashboard = forceReload === "true" || navigationFromMdm === "true";

  if (isNavigationToDashboard) {
    const redirectsStore = useRedirectsStore();
    await redirectsStore.handleRedirectToDashboard(workspaceId);
  }
}

async function saveDataToControlPanel(to: RouteLocationNormalized) {
  if (to.name === RouteName.SETTINGS && to.query["workspaceId"]) {
    const redirectsStore = useRedirectsStore();
    await redirectsStore.handleRedirectToDashboard(to.query["workspaceId"] as string);
  }
}
